// src/components/AdminOverrideBanner.js
//
// A fixed banner visible only to staff/superuser accounts when a global admin
// override is currently active.  It shows the current mode and provides a
// one-click disable so admins don't accidentally leave the override running.

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAdminOverride, OVERRIDE_MODES } from '../contexts/AdminOverrideContext';
import { useAuth } from '../contexts/AuthContext';
import './AdminOverrideBanner.css';

const MODE_LABELS = {
  [OVERRIDE_MODES.UNLOCK_ALL]: 'Unlock Everything',
  [OVERRIDE_MODES.FORCE_PRO]: 'Force Pro Tier',
};

export default function AdminOverrideBanner() {
  const { user } = useAuth();
  const { overrideEnabled, overrideMode, setOverride } = useAdminOverride();
  const [disabling, setDisabling] = useState(false);

  // Only render when override is active.
  // Show to confirmed admins (is_staff/is_superuser = true) OR to logged-in
  // users where admin status is unknown because the UserSerializer doesn't
  // include is_staff yet. Non-admin users (explicitly false) are excluded.
  const isExplicitlyNotAdmin = user?.is_staff === false && user?.is_superuser === false;
  if (!user || isExplicitlyNotAdmin || !overrideEnabled || overrideMode === OVERRIDE_MODES.NORMAL) {
    return null;
  }

  const handleDisable = async () => {
    setDisabling(true);
    try {
      await setOverride({ enabled: false, mode: OVERRIDE_MODES.NORMAL });
    } catch (err) {
      console.error('[AdminOverride] Failed to disable override:', err.message);
    } finally {
      setDisabling(false);
    }
  };

  return (
    <div className="aob" role="alert" aria-live="polite">
      <span className="aob__icon" aria-hidden="true">⚠</span>
      <span className="aob__text">
        Admin Override Active — <strong>{MODE_LABELS[overrideMode] ?? overrideMode}</strong>
      </span>
      <Link to="/admin/override" className="aob__manage-link">
        Manage
      </Link>
      <button
        className="aob__disable-btn"
        onClick={handleDisable}
        disabled={disabling}
        aria-label="Disable global override"
      >
        {disabling ? 'Disabling…' : 'Disable'}
      </button>
    </div>
  );
}
